import { ComponentProps, ComponentType, useContext } from "react";
import { useModal } from "react-modal-hook";
import {
  FIRM_COMPARISON_MODAL_WARNINGS,
  FIRMsWithWarnings,
  FIRMWithWarnings,
} from "common-client/utils/firmInfoWarnings";
import { FIRMJurisdiction } from "../../../../generated/graphql";
import {
  Container,
  ContentSection,
  HeaderSection,
} from "../../../Common/__styles__/Modal";
import { TableCell, TableRow } from "../../../Common/__styles__/StripedTable";
import Modal from "../../../Common/Modal";
import { LayerContext } from "../../../Maps/layers";
import { WarningsWrapper } from "./__styles__/FIRMComparisonModal";
import { FIRMInfoContext } from "./FIRMInfoContext";
import FIRMTable from "./FIRMTable";
import { FIRMWarningsBanner } from "./FIRMWarningsBanner";

export const useFIRMComparisonModal = (
  props: ComponentProps<typeof FIRMComparison>,
) => {
  const context = useContext(FIRMInfoContext);
  const layerContext = useContext(LayerContext);
  // We are providing a new, isolated `PropertyOverviewContext` because <Modal /> calls `Modal.setAppElement("#app")`,
  // which means it lives at the root of our html instead of a child of the caller. This means it is outside
  // the existing context. However, we can use this to our advantage by setting a different value
  // for activeFirmIds, so we can display all of them in the modal
  const activeFirmIds = context.firms.map(firm => firm.id);
  const [showModal, closeModal] = useModal(
    () => (
      <Modal onRequestClose={closeModal}>
        <LayerContext.Provider value={layerContext}>
          <FIRMInfoContext.Provider value={{ ...context, activeFirmIds }}>
            <FIRMComparison {...props} />
          </FIRMInfoContext.Provider>
        </LayerContext.Provider>
      </Modal>
    ),
    [props],
  );

  return [showModal, closeModal];
};

function TableFooter({
  Blank,
  firms,
}: {
  Blank: ComponentType;
  firms: FIRMsWithWarnings;
}) {
  const jurisdictionCell = ({
    firm,
    jurisdiction,
  }: {
    firm: FIRMWithWarnings;
    jurisdiction: FIRMJurisdiction;
  }) => {
    const cell = firm.jurisdictions.includes(jurisdiction) ? "Yes" : <Blank />;
    return (
      <TableCell key={`${jurisdiction}-${firm.id}`} size="wide">
        {cell}
      </TableCell>
    );
  };

  return (
    <>
      <TableRow>
        <TableCell>NFIP Insurance FIRM</TableCell>
        {firms.map(firm =>
          jurisdictionCell({ firm, jurisdiction: FIRMJurisdiction.NFIP }),
        )}
      </TableRow>
      <TableRow>
        <TableCell>Regulatory FIRM</TableCell>
        {firms.map(firm =>
          jurisdictionCell({ firm, jurisdiction: FIRMJurisdiction.REGULATORY }),
        )}
      </TableRow>
    </>
  );
}

export type FIRMComparisonProps = Omit<
  ComponentProps<typeof FIRMTable>,
  "showTitle" | "Footer"
>;

function FIRMComparison({
  property,
  account,
  onFIRMEdit,
  onFIRMClick,
  section,
}: FIRMComparisonProps) {
  const { firms } = useContext(FIRMInfoContext);

  return (
    <Container width={"wide"} data-testid="firm-comparison-modal">
      <HeaderSection>
        <h1>{section.label}</h1>
      </HeaderSection>
      <ContentSection style={{ padding: 0 }}>
        <FIRMTable
          account={account}
          property={property}
          onFIRMEdit={onFIRMEdit}
          onFIRMClick={onFIRMClick}
          showTitle={false}
          size="wide"
          isComparisonModal={true}
          Footer={props => TableFooter({ ...props, firms })}
          section={section}
        />

        <WarningsWrapper>
          <FIRMWarningsBanner
            firms={firms}
            propertyId={property?.id}
            warningTextConfig={FIRM_COMPARISON_MODAL_WARNINGS}
          />
        </WarningsWrapper>
      </ContentSection>
    </Container>
  );
}

export default FIRMComparison;
